"use client";

import { useState } from "react";
import { Icon, Arrow } from "./Icon";

export interface SpeakToExpertFormLabels {
  contact_info_title: string;
  needs_section_title: string;
  field_first_name: string;
  field_first_name_placeholder: string;
  field_last_name: string;
  field_last_name_placeholder: string;
  field_position: string;
  field_position_placeholder: string;
  field_company: string;
  field_company_placeholder: string;
  field_email: string;
  field_email_placeholder: string;
  field_phone: string;
  field_phone_placeholder: string;
  field_topic: string;
  field_topic_placeholder: string;
  field_industry: string;
  field_industry_placeholder: string;
  field_industry_other_label: string;
  field_message: string;
  field_message_help: string;
  field_message_placeholder: string;
  topics: string[];
  time_zones: string[];
  times: string[];
  submit_label: string;
  submit_sending: string;
  success_title: string;
  success_subtitle: string;
  success_again_label: string;
  error_generic: string;
  preferred_connection_title: string;
  preferred_connection_help: string;
  connection_call_title: string;
  connection_call_text: string;
  connection_meeting_title: string;
  connection_meeting_text: string;
  connection_email_title: string;
  connection_email_text: string;
  best_time_title: string;
  field_time_zone: string;
  field_time_zone_placeholder: string;
  field_date: string;
  field_time: string;
  field_time_placeholder: string;
}

type Connection = "call" | "meeting" | "email";

export default function SpeakToExpertForm({
  industries,
  labels,
}: {
  industries: { slug: string; title: string }[];
  labels: SpeakToExpertFormLabels;
}) {
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [error, setError] = useState("");
  const [connection, setConnection] = useState<Connection>("call");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("sending");
    setError("");
    const form = e.currentTarget;
    const fd = new FormData(form);
    const data = {
      full_name: `${fd.get("first_name")} ${fd.get("last_name")}`,
      job_title: fd.get("position"),
      company: fd.get("company"),
      industry: fd.get("industry"),
      email: fd.get("email"),
      phone: fd.get("phone"),
      message: [
        `Connection: ${connection}`,
        `Topic: ${fd.get("topic")}`,
        `Time Zone: ${fd.get("time_zone")}`,
        `Date: ${fd.get("date")}`,
        `Time: ${fd.get("time")}`,
        "",
        fd.get("message"),
      ].filter(Boolean).join("\n"),
    };
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || labels.error_generic);
      }
      setStatus("ok");
      form.reset();
    } catch (err: any) {
      setStatus("error");
      setError(err.message);
    }
  }

  if (status === "ok") {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-8 text-center dark:border-emerald-800/60 dark:bg-emerald-950/40">
        <h3 className="text-lg font-bold text-navy dark:text-emerald-100">{labels.success_title}</h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-emerald-200/80">{labels.success_subtitle}</p>
        <button onClick={() => setStatus("idle")} className="mt-5 text-sm font-semibold text-brand-red dark:text-brand-red-bright">
          {labels.success_again_label}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-8 lg:grid-cols-[2fr_1fr]">
      {/* Left: main fields */}
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-bold uppercase text-navy">{labels.contact_info_title}</h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field name="first_name" label={labels.field_first_name} required placeholder={labels.field_first_name_placeholder} />
            <Field name="last_name" label={labels.field_last_name} required placeholder={labels.field_last_name_placeholder} />
            <Field name="position" label={labels.field_position} required placeholder={labels.field_position_placeholder} />
            <Field name="company" label={labels.field_company} required placeholder={labels.field_company_placeholder} />
            <Field name="email" label={labels.field_email} type="email" required placeholder={labels.field_email_placeholder} />
            <Field name="phone" label={labels.field_phone} type="tel" required placeholder={labels.field_phone_placeholder} />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-bold uppercase text-navy">{labels.needs_section_title}</h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <Label>{labels.field_topic} <Req /></Label>
              <select name="topic" required defaultValue="" className="input">
                <option value="" disabled>{labels.field_topic_placeholder}</option>
                {labels.topics.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label>{labels.field_industry} <Req /></Label>
              <select name="industry" required defaultValue="" className="input">
                <option value="" disabled>{labels.field_industry_placeholder}</option>
                {industries.map((i) => <option key={i.slug} value={i.title}>{i.title}</option>)}
                <option value="Other">{labels.field_industry_other_label}</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <Label>{labels.field_message}</Label>
            <p className="-mt-1 mb-2 text-xs text-gray-500">{labels.field_message_help}</p>
            <textarea name="message" rows={5} placeholder={labels.field_message_placeholder} className="input resize-none" />
          </div>
        </div>

        {status === "error" && <p className="text-sm text-brand-red">{error}</p>}

        <div>
          <button type="submit" disabled={status === "sending"} className="btn-red px-8 disabled:opacity-60">
            {status === "sending" ? labels.submit_sending : <>{labels.submit_label} <Arrow /></>}
          </button>
        </div>
      </div>

      {/* Right: preferred connection */}
      <aside className="rounded-lg bg-gray-50 p-5 ring-1 ring-gray-100">
        <h3 className="text-sm font-bold uppercase text-navy">{labels.preferred_connection_title}</h3>
        <p className="mt-1 text-xs text-gray-600">{labels.preferred_connection_help}</p>

        <div className="mt-4 space-y-3">
          <ConnectionOption
            checked={connection === "call"}
            onChange={() => setConnection("call")}
            tint="red"
            icon="people"
            title={labels.connection_call_title}
            text={labels.connection_call_text}
          />
          <ConnectionOption
            checked={connection === "meeting"}
            onChange={() => setConnection("meeting")}
            tint="navy"
            icon="compliance"
            title={labels.connection_meeting_title}
            text={labels.connection_meeting_text}
          />
          <ConnectionOption
            checked={connection === "email"}
            onChange={() => setConnection("email")}
            tint="navy"
            icon="monitor"
            title={labels.connection_email_title}
            text={labels.connection_email_text}
          />
        </div>

        <hr className="my-5 border-gray-200" />

        <h3 className="text-sm font-bold uppercase text-navy">{labels.best_time_title}</h3>
        <div className="mt-3 space-y-3">
          <div>
            <Label>{labels.field_time_zone}</Label>
            <select name="time_zone" defaultValue="" className="input">
              <option value="" disabled>{labels.field_time_zone_placeholder}</option>
              {labels.time_zones.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label>{labels.field_date}</Label>
            <input name="date" type="date" className="input" />
          </div>
          <div>
            <Label>{labels.field_time}</Label>
            <select name="time" defaultValue="" className="input">
              <option value="" disabled>{labels.field_time_placeholder}</option>
              {labels.times.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      </aside>
    </form>
  );
}

function ConnectionOption({
  checked, onChange, tint, icon, title, text,
}: {
  checked: boolean; onChange: () => void;
  tint: "red" | "navy"; icon: any; title: string; text: string;
}) {
  return (
    <label className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 ${checked ? "border-brand-red bg-white shadow-xs" : "border-gray-200 bg-white"}`}>
      <input type="radio" name="connection" checked={checked} onChange={onChange} className="mt-1.5 h-4 w-4 accent-brand-red" />
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${tint === "red" ? "bg-brand-red/10 text-brand-red" : "bg-navy/10 text-navy"}`}>
        <Icon name={icon} size={18} />
      </span>
      <div>
        <p className="text-sm font-bold text-navy">{title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-gray-600">{text}</p>
      </div>
    </label>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <label className="mb-1.5 block text-sm font-semibold text-navy">{children}</label>;
}
function Req() { return <span className="text-brand-red">*</span>; }
function Field({ name, label, type = "text", required = false, placeholder }: { name: string; label: string; type?: string; required?: boolean; placeholder?: string }) {
  return (
    <div>
      <Label>{label} {required && <Req />}</Label>
      <input name={name} type={type} required={required} placeholder={placeholder} className="input" />
    </div>
  );
}
